export const DEFAULT_MAX_CONTACT_INPUT_DIGITS = 20
export const WEEKLY_CONTACT_MULTIPLIER = 7
export const MONTHLY_CONTACT_MULTIPLIER = 28

export const PREPAID_CONTACT_FIELDS = new Set([
    'LeadspeekLeadsPerday',
    'LeadspeekLeadsPermonth',
    'EnhanceLeadsPerday',
    'EnhanceLeadsPermonth',
    'B2bLeadsPerday',
    'B2bLeadsPermonth',
])

export function isPrepaidContactField(field) {
    return PREPAID_CONTACT_FIELDS.has(field)
}

export function toDigitsOnly(rawValue) {
    if (rawValue == null || rawValue === '') {
        return ''
    }
    if (typeof rawValue === 'number') {
        if (!Number.isFinite(rawValue) || Math.abs(rawValue) > Number.MAX_SAFE_INTEGER) {
            return ''
        }
        rawValue = String(Math.trunc(rawValue))
    } else {
        rawValue = String(rawValue)
    }
    if (/e/i.test(rawValue)) {
        return ''
    }
    return rawValue.replace(/\D/g, '')
}

export function addDigitStrings(leftDigits, rightDigits) {
    const maxLength = Math.max(leftDigits.length, rightDigits.length)
    const left = leftDigits.padStart(maxLength, '0')
    const right = rightDigits.padStart(maxLength, '0')
    let carry = 0
    let sumDigits = ''

    for (let index = maxLength - 1; index >= 0; index--) {
        const columnSum = parseInt(left.charAt(index), 10) + parseInt(right.charAt(index), 10) + carry
        sumDigits = String(columnSum % 10) + sumDigits
        carry = Math.floor(columnSum / 10)
    }

    if (carry) {
        sumDigits = String(carry) + sumDigits
    }

    const trimmed = sumDigits.replace(/^0+/, '')
    return trimmed || '0'
}

export function multiplyDigitString(digitString, multiplier) {
    let totalDigits = '0'

    for (let index = digitString.length - 1, placePower = 0; index >= 0; index--, placePower++) {
        const digit = parseInt(digitString.charAt(index), 10)
        if (!digit) {
            continue
        }
        const partialProduct = String(digit * multiplier) + '0'.repeat(placePower)
        totalDigits = addDigitStrings(totalDigits, partialProduct)
    }

    return totalDigits
}

export function sanitizeContactsInput(rawValue, maxDigits) {
    const limit = maxDigits == null ? DEFAULT_MAX_CONTACT_INPUT_DIGITS : maxDigits
    const digits = toDigitsOnly(rawValue)
    if (!digits) {
        return ''
    }
    return digits.slice(0, limit)
}

export function formatContactsDisplayNumber(digitString) {
    if (!digitString || digitString === '0') {
        return '0'
    }
    if (digitString.length >= 16) {
        return digitString.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
    return digitString
}

export function formatContactsToBuyDisplay(contactsPerDay, isMonthlyBilling, options) {
    const opts = options || {}
    const maxDigits = opts.maxDigits == null ? DEFAULT_MAX_CONTACT_INPUT_DIGITS : opts.maxDigits
    const weeklyMultiplier = opts.weeklyMultiplier == null ? WEEKLY_CONTACT_MULTIPLIER : opts.weeklyMultiplier
    const monthlyMultiplier = opts.monthlyMultiplier == null ? MONTHLY_CONTACT_MULTIPLIER : opts.monthlyMultiplier
    const sanitized = sanitizeContactsInput(contactsPerDay, maxDigits)
    if (!sanitized || sanitized === '0') {
        return '0'
    }
    const multiplier = isMonthlyBilling ? monthlyMultiplier : weeklyMultiplier
    return formatContactsDisplayNumber(multiplyDigitString(sanitized, multiplier))
}

export function formatContactsCountDisplay(rawValue, maxDigits) {
    return formatContactsDisplayNumber(sanitizeContactsInput(rawValue, maxDigits))
}

export function computeContactsToBuyDigits(contactsPerDay, isMonthlyBilling, options) {
    const opts = options || {}
    const maxDigits = opts.maxDigits == null ? DEFAULT_MAX_CONTACT_INPUT_DIGITS : opts.maxDigits
    const weeklyMultiplier = opts.weeklyMultiplier == null ? WEEKLY_CONTACT_MULTIPLIER : opts.weeklyMultiplier
    const monthlyMultiplier = opts.monthlyMultiplier == null ? MONTHLY_CONTACT_MULTIPLIER : opts.monthlyMultiplier
    const sanitized = sanitizeContactsInput(contactsPerDay, maxDigits)
    if (!sanitized || sanitized === '0') {
        return '0'
    }
    const multiplier = isMonthlyBilling ? monthlyMultiplier : weeklyMultiplier
    return multiplyDigitString(sanitized, multiplier)
}

export function parseDecimalValue(rawValue) {
    const str = String(rawValue == null ? '' : rawValue).replace(/,/g, '').trim()
    if (!str || str === '') {
        return { digits: '0', scale: 0 }
    }
    if (/e/i.test(str)) {
        return { digits: '0', scale: 0 }
    }
    const parts = str.split('.')
    const intPart = (parts[0] || '0').replace(/\D/g, '') || '0'
    const fracPart = (parts[1] || '').replace(/\D/g, '')
    const digits = (intPart + fracPart).replace(/^0+/, '') || '0'
    return { digits, scale: fracPart.length }
}

export function multiplyDigitStrings(multiplicand, multiplier) {
    const left = String(multiplicand).replace(/^0+/, '') || '0'
    const right = String(multiplier).replace(/^0+/, '') || '0'
    if (left === '0' || right === '0') {
        return '0'
    }

    const result = new Array(left.length + right.length).fill(0)
    for (let i = left.length - 1; i >= 0; i--) {
        for (let j = right.length - 1; j >= 0; j--) {
            const product = parseInt(left.charAt(i), 10) * parseInt(right.charAt(j), 10)
            const position = i + j + 1
            const sum = product + result[position]
            result[position] = sum % 10
            result[position - 1] += Math.floor(sum / 10)
        }
    }

    let start = 0
    while (start < result.length - 1 && result[start] === 0) {
        start++
    }
    return result.slice(start).join('')
}

export function formatMoneyFromScaledInteger(scaledDigits, scale) {
    const digits = String(scaledDigits).replace(/^0+/, '') || '0'
    const decimalPlaces = scale == null ? 0 : scale

    if (decimalPlaces === 0) {
        return formatContactsDisplayNumber(digits)
    }

    const padded = digits.padStart(decimalPlaces + 1, '0')
    const intPart = padded.slice(0, -decimalPlaces).replace(/^0+/, '') || '0'
    const fracPart = padded.slice(-decimalPlaces)
    return `${formatContactsDisplayNumber(intPart)}.${fracPart}`
}

export function multiplyContactsByUnitPrice(contactCount, unitPrice) {
    const contacts = sanitizeContactsInput(contactCount)
    if (!contacts || contacts === '0') {
        return '0.00'
    }
    const price = parseDecimalValue(unitPrice)
    if (price.digits === '0') {
        return '0.00'
    }
    const product = multiplyDigitStrings(contacts, price.digits)
    return formatMoneyFromScaledInteger(product, price.scale)
}

export function computePeriodContactDigits(contactsPerDay, periodMultiplier) {
    const sanitized = sanitizeContactsInput(contactsPerDay)
    if (!sanitized || sanitized === '0') {
        return '0'
    }
    const multiplier = periodMultiplier == null ? 1 : periodMultiplier
    return multiplyDigitString(sanitizeContactsInput(sanitized), WEEKLY_CONTACT_MULTIPLIER * multiplier)
}

export function computeCampaignContactCost(contactsPerDay, unitPrice, periodMultiplier) {
    const periodContacts = computePeriodContactDigits(contactsPerDay, periodMultiplier)
    return multiplyContactsByUnitPrice(periodContacts, unitPrice)
}

export function addMoneyAmounts(...values) {
    let maxScale = 0
    const normalized = values.map((value) => {
        const parsed = parseDecimalValue(value)
        maxScale = Math.max(maxScale, parsed.scale)
        return parsed
    })

    let sumDigits = '0'
    normalized.forEach((item) => {
        const adjusted = item.scale < maxScale
            ? item.digits + '0'.repeat(maxScale - item.scale)
            : item.digits
        sumDigits = addDigitStrings(sumDigits, adjusted)
    })

    return formatMoneyFromScaledInteger(sumDigits, maxScale)
}

export function divideMoneyAmount(value, divisor) {
    const parsed = parseDecimalValue(value)
    const divisorDigits = String(divisor).replace(/\D/g, '') || '1'
    if (parsed.digits === '0' || divisorDigits === '0') {
        return '0.00'
    }

    let remainder = 0
    let quotient = ''
    const source = parsed.digits
    for (let index = 0; index < source.length; index++) {
        remainder = remainder * 10 + parseInt(source.charAt(index), 10)
        const digit = Math.floor(remainder / parseInt(divisorDigits, 10))
        quotient += String(digit)
        remainder %= parseInt(divisorDigits, 10)
    }

    quotient = quotient.replace(/^0+/, '') || '0'
    return formatMoneyFromScaledInteger(quotient, parsed.scale)
}

export function formatLargePrice(value) {
    if (value == null || value === '') {
        return '0.00'
    }

    const display = String(value).trim()
    if (display.includes(',') && /\.\d{1,2}$/.test(display)) {
        const parts = display.split('.')
        return `${parts[0]}.${parts[1].padEnd(2, '0').slice(0, 2)}`
    }

    const raw = display.replace(/,/g, '').trim()
    if (!raw) {
        return '0.00'
    }

    if (raw.includes('.')) {
        const parsed = parseDecimalValue(raw)
        if (parsed.digits !== '0') {
            return formatMoneyFromScaledInteger(parsed.digits, parsed.scale)
        }
    }

    const digits = raw.replace(/\D/g, '') || '0'
    if (digits === '0') {
        return '0.00'
    }

    if (/e/i.test(raw)) {
        return '0.00'
    }

    if (digits.length < 16) {
        const numericValue = Number(digits)
        if (Number.isFinite(numericValue)) {
            return numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        }
    }

    return `${formatContactsDisplayNumber(digits)}.00`
}
